/**
 * Attribution-relevant URL params, parsed once per page load at init and
 * stored on the SDK state. Common analytics params and vendor resolution read
 * from this snapshot instead of re-parsing `location` per event.
 */

import { getCookie } from '../../util/cookies';

/** Yoto host cookie for the assigned AB test version when `?abtv=` is absent. */
const AB_TEST_COOKIE = 'kls_abtv';

export interface PageParams {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  utmTerm: string;
  /** Media-buying id (`mbid`). */
  mediaBuyingId: string;
  /**
   * Platform click id — `tblci`, else `dicbo`, else `mbclid`.
   * Also mirrored into `/dye` `mbclid` / attribution `tblci`.
   */
  platformClickId: string;
  /** User value objective (`uvc`), '0' when absent. */
  userValueObjective: string;
  /** Channel override (`ch`) — `/dye` `aci` when present. */
  channel: string;
  /** AB test value (`abtv`), default `'a'`. */
  abTestValue: string;
  /** Outbrain CPC (`obcpc`) — plain float cost from the ad URL. */
  outbrainCpc?: string;
  /**
   * Taboola CPC (`tblcpc`) — opaque encrypted cost string; analytics decrypts
   * server-side using ad-account `pixel_id` (`tblpxid`) + `pixel_tag_id` (`pxtid`).
   */
  taboolaCpc?: string;
}

/**
 * Resolve AB test value: `?abtv=` wins, else non-default `kls_abtv` cookie
 * (same host cookie yoto writes after assignment), else `'a'`.
 */
export function resolveAbTestValue(urlAbtv: string | null | undefined): string {
  const fromUrl = urlAbtv?.trim();
  if (fromUrl) return fromUrl;

  const fromCookie = getCookie(AB_TEST_COOKIE)?.trim();
  // Yoto treats cookie value `a` as unset legacy default, not an active variant.
  if (fromCookie && fromCookie !== 'a') return fromCookie;

  return 'a';
}

export function resolvePageParams(search?: string): PageParams {
  const raw = search ?? (typeof location !== 'undefined' ? location.search : '');
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(raw);
  } catch {
    params = new URLSearchParams();
  }
  const get = (key: string, fallback = ''): string => params.get(key) ?? fallback;

  const outbrainCpc = get('obcpc') || undefined;
  const taboolaCpc = get('tblcpc') || undefined;

  return {
    utmSource: get('utm_source') || get('utms'),
    utmMedium: get('utm_medium') || get('utmm'),
    utmCampaign: get('utm_campaign'),
    utmContent: get('utm_content'),
    utmTerm: get('utm_term'),
    mediaBuyingId: get('mbid'),
    platformClickId: get('tblci') || get('dicbo') || get('mbclid'),
    userValueObjective: get('uvc', '0'),
    channel: get('ch'),
    abTestValue: resolveAbTestValue(get('abtv')),
    ...(outbrainCpc ? { outbrainCpc } : {}),
    ...(taboolaCpc ? { taboolaCpc } : {}),
  };
}
